export function readICS(text) {
  const lines = text.replace(/\r?\n[ \t]/g, '').split(/\r?\n/);
  const events = []; let event;
  for (const line of lines) {
    if (line === 'BEGIN:VEVENT') { event = {}; continue; }
    if (line === 'END:VEVENT') { if (event) events.push(event); event = null; continue; }
    if (!event) continue;
    const colon = line.indexOf(':'); if (colon < 0) continue;
    const key = line.slice(0, colon); const value = line.slice(colon + 1);
    const name = key.split(';')[0];
    if (!event[name]) event[name] = { value, tz: /(?:^|;)TZID=([^;:]+)/.exec(key)?.[1]?.replace(/^"|"$/g, '') || '' };
  }
  if (events.length !== 1 || events[0].RRULE || events[0].RDATE) throw new Error('A single workshop event is required for the Google link.');
  const e = events[0]; const start = e.DTSTART; const end = e.DTEND;
  const valid = v => /^\d{8}(?:T\d{6}Z?)?$/.test(v || '');
  if (!valid(start?.value) || !valid(end?.value)) throw new Error('The calendar needs a start and end.');
  if (start.value.length !== end.value.length || start.tz !== end.tz || end.value <= start.value) throw new Error('Calendar dates must use the same time zone and an end after the start.');
  const decode = v => (v || '').replace(/\\([nN,;\\])/g, (_, c) => /n/i.test(c) ? '\n' : c);
  return { start: start.value, end: end.value, timezone: start.tz, title: decode(e.SUMMARY?.value), description: decode(e.DESCRIPTION?.value), location: decode(e.LOCATION?.value) };
}
export function googleURL(event, fallback = {}) {
  const url = new URL('https://calendar.google.com/calendar/render');
  url.searchParams.set('action','TEMPLATE');
  url.searchParams.set('text',event.title || fallback.title || 'Workshop');
  url.searchParams.set('dates',`${event.start}/${event.end}`);
  url.searchParams.set('details',[event.description || fallback.description, fallback.url].filter(Boolean).join('\n\n'));
  url.searchParams.set('location',event.location || fallback.location || '');
  if (!event.start.endsWith('Z') && event.start.length > 8) url.searchParams.set('ctz',event.timezone || fallback.timezone || 'America/New_York');
  return url.toString();
}
export function configuredEvent(config) {
  const stamp = value => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})$/.test(value || '')) throw new Error('Use ISO date-times with a UTC offset.');
    const date = new Date(value); if (!Number.isFinite(date.getTime())) throw new Error('Invalid date-time.');
    return date.toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,'');
  };
  const start = stamp(config.start), end = stamp(config.end);
  if (end <= start) throw new Error('The workshop end must follow its start.');
  return { ...config, start, end };
}
export function textEvent(config) {
  const text = String(config.dateText || '').replace(/\u00a0/g, ' ').trim();
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const match = /^(?:(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday),?\s*)?(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{1,2})(?:,?\s+(\d{4}))?\s*[⋅·•|@]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–—]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i.exec(text);
  if (!match) throw new Error('Use Sunday, November 8⋅11:00am – 12:00pm (year optional).');
  const year = Number(match[4] || config.defaultYear || 2026), month = months.findIndex(m => m.toLowerCase() === match[2].toLowerCase()), day = Number(match[3]);
  const date = new Date(Date.UTC(year, month, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) throw new Error('The workshop date is invalid.');
  if (match[1] && days[date.getUTCDay()].toLowerCase() !== match[1].toLowerCase()) throw new Error('The weekday does not match the workshop date/year.');
  const clock = (hour, minute, period) => {
    hour = Number(hour); minute = Number(minute || 0);
    if (hour < 1 || hour > 12 || minute > 59) throw new Error('Invalid workshop time.');
    return [(hour % 12) + (period.toLowerCase() === 'pm' ? 12 : 0), minute];
  };
  // Calendar-style ranges often share a trailing am/pm: 6–8pm or 11–1pm.
  const end = clock(match[8],match[9],match[10]);
  let start = clock(match[5],match[6],match[7] || match[10]);
  if (!match[7] && start[0]*60+start[1] >= end[0]*60+end[1] && match[10].toLowerCase() === 'pm') {
    start = clock(match[5],match[6],'am');
  }
  if (end[0]*60+end[1] <= start[0]*60+start[1]) throw new Error('The end must follow the start on the same day.');
  const zone = config.timezone || 'America/New_York';
  const format = new Intl.DateTimeFormat('en-US', { timeZone:zone, hourCycle:'h23', year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit' });
  const parts = ms => Object.fromEntries(format.formatToParts(new Date(ms)).filter(p => p.type !== 'literal').map(p => [p.type, Number(p.value)]));
  const utc = (hour, minute) => {
    const naive = Date.UTC(year,month,day,hour,minute,0);
    const offsets = new Set([-86400000,0,86400000].map(delta => {
      const ms = naive+delta, p=parts(ms);
      return Date.UTC(p.year,p.month-1,p.day,p.hour,p.minute,p.second)-ms;
    }));
    const matches = [...offsets].map(offset=>naive-offset).filter(ms=>{
      const p=parts(ms); return p.year===year && p.month===month+1 && p.day===day && p.hour===hour && p.minute===minute;
    });
    if (matches.length !== 1) throw new Error('This local time is missing or ambiguous at a daylight-saving transition.');
    return new Date(matches[0]).toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,'');
  };
  return { ...config, start:utc(...start), end:utc(...end) };
}
export function eventICS(event, id) {
  const escape = s => String(s || '').replace(/\\/g,'\\\\').replace(/\r?\n/g,'\\n').replace(/,/g,'\\,').replace(/;/g,'\\;');
  const fold = line => {
    let out = '', chunk = '', length = 0;
    for (const char of line) {
      const size = new TextEncoder().encode(char).length;
      if (length + size > 75) { out += chunk + '\r\n'; chunk = ' '; length = 1; }
      chunk += char; length += size;
    }
    return out + chunk;
  };
  return ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//Manchester Craft Market//Workshops//EN','CALSCALE:GREGORIAN','BEGIN:VEVENT',`UID:${escape(id)}@manchestercraftmarket.com`,`DTSTAMP:${new Date().toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,'')}`,`DTSTART:${event.start}`,`DTEND:${event.end}`,`SUMMARY:${escape(event.title)}`,`LOCATION:${escape(event.location)}`,`DESCRIPTION:${escape([event.description,event.url].filter(Boolean).join('\n\n'))}`,'END:VEVENT','END:VCALENDAR'].map(fold).join('\r\n')+'\r\n';
}
class McmWorkshopCalendar extends HTMLElement {
  async connectedCallback() {
    if (this.running) return; this.running = true;
    const config = JSON.parse(this.dataset.config || '{}');
    const google = this.querySelector('[data-google]'); const download = this.querySelector('[data-ics]');
    this.abort = new AbortController();
    try {
      let event;
      if (config.file) {
        const response = await fetch(config.file, { signal:this.abort.signal });
        if (!response.ok) throw new Error('Calendar file could not be read.');
        event = readICS(await response.text());
      } else {
        event = config.start || config.end ? configuredEvent(config) : textEvent(config);
        this.objectURL = URL.createObjectURL(new Blob([eventICS(event,config.id)],{type:'text/calendar;charset=utf-8'}));
        if (download) { download.href=this.objectURL; download.download='workshop.ics'; download.hidden=false; }
      }
      if (!this.isConnected) return;
      if (google) { google.href=googleURL(event,config); google.hidden=false; }
    } catch (error) {
      if (error.name !== 'AbortError' && this.dataset.editor === 'true') {
        const status=this.querySelector('[data-calendar-status]'); if(status) { status.hidden=false; status.textContent=error.message; }
      }
    }
  }
  disconnectedCallback() { this.abort?.abort(); if(this.objectURL) URL.revokeObjectURL(this.objectURL); this.running=false; }
}
if (!customElements.get('mcm-workshop-calendar')) customElements.define('mcm-workshop-calendar',McmWorkshopCalendar);
